import { useTranslation } from 'react-i18next';
import { S, BtnPrimary, BtnSecondary } from '../regureStyles';
import { useFeature } from '../../hooks/useAccess';

const AddForm = ({ title, btnText, setShowAddForm, newBroker, setNewBroker, submitting, handleAddBroker }) => {
  const { t } = useTranslation();
  // A "Commission Plan" input on a tenant that does not run commissions asks
  // the admin to configure a module they do not have, and whatever they typed
  // would drive nothing. Same flag as the Portfolio Summary field and the
  // Commission History section on the detail view.
  const commissionsEnabled = useFeature('commissions');
  const fields = [
    { label: t('screens.brokers.fieldName',      { defaultValue: 'Broker Name *' }),      key: 'name',     placeholder: t('screens.brokers.phName',     { defaultValue: 'e.g. Helvetica Partners AG' }) },
    { label: t('screens.brokers.fieldContact',   { defaultValue: 'Primary Contact' }),    key: 'contact',  placeholder: t('screens.brokers.phContact',  { defaultValue: 'Full name' }) },
    { label: t('screens.brokers.fieldEmail',     { defaultValue: 'Email' }),              key: 'email',    placeholder: t('screens.brokers.phEmail',    { defaultValue: 'contact@broker.ch' }) },
    { label: t('screens.brokers.fieldPhone',     { defaultValue: 'Phone' }),              key: 'phone',    placeholder: t('screens.brokers.phPhone',    { defaultValue: '+41 44 000 00 00' }) },
    // Street and postal code. The detail card has always RENDERED the full
    // mailing address (address + postalCode + city + canton) and the importer
    // has always WRITTEN all four, but only city and canton ever had an input
    // — so a street address could be imported and displayed and never
    // corrected, which read to the user as "the address will not save".
    // Absent keys are stripped from the PATCH, so the stored street survived;
    // it was unreachable, not lost. See brokerFieldsSurviveRoundTrip.test.mjs,
    // which now asserts the reverse direction too: what the card shows, the
    // form must be able to edit.
    { label: t('screens.brokers.fieldAddress',    { defaultValue: 'Street Address' }),     key: 'address',    placeholder: t('screens.brokers.phAddress',    { defaultValue: 'e.g. Route de St-Julien 277' }) },
    { label: t('screens.brokers.fieldPostalCode', { defaultValue: 'Postal Code' }),        key: 'postalCode', placeholder: t('screens.brokers.phPostalCode', { defaultValue: 'e.g. 1258' }) },
    { label: t('screens.brokers.fieldCity',      { defaultValue: 'City' }),               key: 'city',     placeholder: t('screens.brokers.phCity',     { defaultValue: 'Zurich' }) },
    { label: t('screens.brokers.fieldCanton',    { defaultValue: 'Canton' }),             key: 'canton',   placeholder: t('screens.brokers.phCanton',   { defaultValue: 'ZH' }) },
    // Regulatory registration. Deliberately an AUTHORITY plus a NUMBER rather
    // than a single "FINMA no." field: a register number is a property every
    // supervised intermediary has, and naming the input after one regulator
    // would force the next jurisdiction into a lie. The placeholder carries
    // the Swiss default without the schema committing to it.
    { label: t('screens.brokers.fieldRegAuthority', { defaultValue: 'Regulatory Authority' }), key: 'registrationAuthority', placeholder: t('screens.brokers.phRegAuthority', { defaultValue: 'e.g. FINMA' }) },
    { label: t('screens.brokers.fieldRegNumber',    { defaultValue: 'Registration no.' }),     key: 'registrationNumber',    placeholder: t('screens.brokers.phRegNumber',    { defaultValue: 'e.g. F01234567' }) },
    ...(commissionsEnabled
      ? [{ label: t('screens.brokers.fieldCommPlan', { defaultValue: 'Commission Plan' }), key: 'commPlan', placeholder: t('screens.brokers.phCommPlan', { defaultValue: 'e.g. Standard (12.5%)' }) }]
      : []),
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: 500, boxShadow: '0 25px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: S.textPrimary, marginBottom: 20 }}>{title || t('screens.brokers.formTitleAdd', { defaultValue: 'Add Broker' })}</div>
        {fields.map(f => (
          <div key={f.key} style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 500, color: S.textSecondary, display: 'block', marginBottom: 5 }}>{f.label}</label>
            <input
              value={newBroker[f.key] || ''}
              onChange={e => setNewBroker(prev => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              style={{
                width: '100%',
                padding: '9px 12px',
                border: `1px solid ${S.borderLight}`,
                borderRadius: 8,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
          <BtnSecondary
            onClick={() => {
              setShowAddForm(false);
              setNewBroker({ name: '', contact: '', email: '', phone: '', address: '', postalCode: '', city: '', canton: '', commPlan: '', registrationAuthority: '', registrationNumber: '' });
            }}
          >
            {t('screens.brokers.formCancel', { defaultValue: 'Cancel' })}
          </BtnSecondary>
          <BtnPrimary onClick={handleAddBroker} disabled={!newBroker.name.trim() || submitting}>
            {submitting
              ? t('screens.brokers.formBtnSaving', { defaultValue: 'Adding...' })
              : (btnText || t('screens.brokers.formBtnAdd', { defaultValue: 'Add Broker' }))}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
};

export default AddForm;
